import React, { useEffect, useState } from 'react';
import { Styles } from '@t2c/shared-fe';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { NextImage, RHFUploadSingleFile } from '@sdf/base';
import {
  CircularProgress,
  Divider,
  Link,
  Slider,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CustomDialogContainer, CustomDialogTitle } from '../../../../Common/dialog';
import { Box, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  getStockBrolls,
  StockBrollItem,
  StockBrollPayload,
  StockBrollResponse,
} from '../../../../services/broll-caption/fetchBrollCaptionData';
import { Broll, BrollType } from '@t2c/ai-video-editor';
import { VideoPlayerForStockVideos } from '../../../video-player/VideoPlayerForStockVideos';
import { useSnackbar } from 'notistack';
import { CustomBRolls } from './CustomBRolls';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  API_CONSTANTS,
  BrollCaptionsAndRenderType,
  FilteringSortingParams,
  PEXELS_URL,
  PIXABAY_URL,
  StockLibrary,
  axiosInstance,
  extractObjectId,
} from '../../../../utils';
import {
  searchCustomBrolls,
  SearchCustomBrollsPaylod,
} from '../../../../services/custom-broll/searchCustomBroll';
import { AiGeneratedMediaTab } from './AiGeneratedMediaTab';
import {
  useFetchUploadId,
  useGetAllAiMediaData,
  useGetAllAvatarCreatedVideos,
  useUpdateUploadData,
} from '../../../../services';
import { UploadTypes } from '../../../../@types';
import { AiGeneratedMediaDialog } from '../../../../components';
import {
  useAppContext,
  useBrollCaption,
  useHandleApiCall,
  usePaginatedAiMediaData,
} from '../../../../hooks';
import { TalkingFaceTab } from './TalkingFaceTab';
import { BrollVideosSection } from 'apps/t2c-fe/components/dashboard/BrollVideosSection';
import { CaptionsTab } from './CaptionsTab';
import { ReferenceType } from 'libs/shared-fe/src/utils/constants';

interface Props {
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
  isImage: boolean;
  updateSelectedBroll?: (value?: Broll) => void;
  broll?: Broll;
  isBrollPage?: boolean;
  isUpscalePage?: boolean;
  showAllmodels?: boolean;
  handleImageSelect?: (url: string) => void;
  handleVideoSelect?: (url: string, refFrom: ReferenceType, refId: string) => void;
}

interface FormValuesProps {
  cover: File | null;
}

export const BrowseBRollModal: React.FC<Props> = ({
  broll,
  updateSelectedBroll,
  shouldOpenDialog,
  handleCloseDialog,
  isImage,
  isBrollPage = false,
  isUpscalePage = false,
  showAllmodels = false,
  handleImageSelect,
  handleVideoSelect,
}) => {
  const tabs =
    isUpscalePage || showAllmodels
      ? {
          MyBrolls: isImage ? 'My Images' : 'My Videos',
          AIGen: isImage ? 'AI Image' : 'AI Video',
          ...(!isImage && {
            TalkingFace: 'Lip Sync',
            Captions: 'Captions & Effects',
            TimelineEditor: 'Timeline Editor',
          }),
        }
      : isBrollPage
      ? {
          MyBrolls: isImage ? 'My Images' : 'My Videos',
          AIGen: isImage ? 'AI Image' : 'AI Video',
          // Pexels: 'Pexels',
          // Pixabay: 'Pixabay',
        }
      : {
          Pexels: 'Pexels',
          // StoryBlocks: 'Story Blocks',
          Pixabay: 'Pixabay',
          MyBrolls: isImage ? 'My Images' : 'My Videos',
          // ShutterStock: 'Shutter Stock',
          AIGen: isImage ? 'AI Image' : 'AI B-rolls/ Videos',
        };

  const [params, setParams] = useState<FilteringSortingParams>({
    sortOrder: 'desc',
    limit: 20,
    sortBy: 'updatedAt',
  });

  const { post } = useHandleApiCall();

  const getPlayableUrlMutation = post(
    API_CONSTANTS.GET_PRESIGNED_URL.key,
    API_CONSTANTS.GET_PRESIGNED_URL.path
  );

  const [stockData, setStockData] = useState<null | StockBrollResponse>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null); // Track selected item
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const [pageSize, setPageSize] = useState(10);

  const [customBrollData, setCustomBrollData] = useState([]);
  const [customBrollCount, setCustomBrollCount] = useState(null);
  // const [showAiGenModel, setShowAiGenModal] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  const { showAiGenModel, handleOpenAiGenModal, handleCloseAiGenModal } = useAppContext();

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { mutateAsync: getUploadedData } = useFetchUploadId();
  const { mutateAsync: updateMetadata } = useUpdateUploadData();

  const handleSubmitAPI = async (pageSize: number) => {
    setLoading(true);

    const payload: StockBrollPayload = {
      keyword: searchText,
      category: isImage ? 'image' : 'video',
      pageSize: pageSize,
      galleryServiceName:
        // tab === tabs.StoryBlocks
        //   ? StockLibrary.StoryBlocks
        //   :
        // tab === tabs.ShutterStock
        //   ? StockLibrary.ShutterStock
        // :
        tab === tabs.Pexels
          ? StockLibrary.Pexels
          : tab === tabs.Pixabay
          ? StockLibrary.Pixabay
          : StockLibrary.Pexels,
    };
    try {
      const response = await getStockBrolls(payload);
      setStockData(response);

      if (searchText && !response?.items?.length) {
        enqueueSnackbar('There are no videos matching your search', { variant: 'error' });
      }
    } catch (error) {
      console.log('Error fetching stock brolls:', error);
      enqueueSnackbar(`Failed to fetch stock brolls. Please try again after some time.`, {
        variant: 'error',
      });
    }
    setLoading(false);
  };

  const handleItemClick = (index: number) => {
    setSelectedIndex(index); // Update selected index
  };

  const fetchFileFromUrl = async (
    url: string,
    filename: string,
    mimeType: string
  ): Promise<File | null> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      console.warn('Failed to fetch file from URL:', error);
      return null;
    }
  };

  const extractImageMetadataFromUrl = async (url: string) => {
    const filename = url.split('/').pop()?.split('?')[0] ?? 'image.jpg';
    const file = await fetchFileFromUrl(url, filename, 'image/jpeg');

    if (!file) return null;

    const dimensions = await new Promise<{ width: number; height: number } | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => {
        console.warn('Failed to extract image dimensions');
        resolve(null);
      };
      img.src = URL.createObjectURL(file);
    });

    if (!dimensions) return null;

    return {
      width: dimensions.width,
      height: dimensions.height,
      size: file.size,
    };
  };

  const extractVideoMetadataFromUrl = async (url: string) => {
    const filename = url.split('/').pop()?.split('?')[0] ?? 'video.mp4';
    const file = await fetchFileFromUrl(url, filename, 'video/mp4');

    if (!file) return null;

    const meta = await new Promise<{ width: number; height: number; duration: number } | null>(
      (resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        const objectUrl = URL.createObjectURL(file);

        video.onloadedmetadata = () => {
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration,
          });
          URL.revokeObjectURL(objectUrl); // clean up
          video.src = '';
        };
        video.onerror = () => {
          console.warn('Failed to extract video metadata');
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };

        video.src = objectUrl;
      }
    );

    if (!meta) return null;

    return {
      width: meta.width,
      height: meta.height,
      duration: meta.duration,
      size: file.size,
    };
  };

  const handleApplyBroll = async (mediaUrl?: string, thumbnailUrl?: string) => {
    // if ((selectedIndex === 0 || selectedIndex) && stockData.items.length) {
    const isCustomBroll = tab === tabs.MyBrolls;
    const isTalkingFace = 'TalkingFace' in tabs && tab === tabs.TalkingFace;
    const isCaptions = 'Captions' in tabs && tab === tabs.Captions;
    const isTimeline = 'TimelineEditor' in tabs && tab === tabs.TimelineEditor;
    setIsApplying(true);
    const selectedBroll = mediaUrl
      ? {}
      : tab === tabs.AIGen
      ? aiMediaData[selectedIndex]
      : 'TalkingFace' in tabs && tab === tabs.TalkingFace
      ? videoData?.pages?.[0]?.data[selectedIndex]
      : 'Captions' in tabs && tab === tabs.Captions
      ? brollCaptions?.pages?.[0]?.brollCaptionVideos[selectedIndex]
      : 'TimelineEditor' in tabs && tab === tabs.TimelineEditor
      ? clipdeck?.pages?.[0]?.brollCaptionVideos[selectedIndex]
      : isCustomBroll
      ? customBrollData[selectedIndex]
      : stockData.items[selectedIndex];

    let talkingfaceUrl = '';
    if (isTalkingFace) {
      const objectId = extractObjectId(selectedBroll?.processedVideo);
      talkingfaceUrl = await getPlayableUrlMutation.mutateAsync({
        format: 'video', //  video only
        objectId: objectId,
        name: `${selectedBroll?.name || objectId}.mp4`,
      });
    }

    if (isBrollPage || isUpscalePage) {
      const url = isCustomBroll
        ? selectedBroll.uploads.url
        : isTalkingFace
        ? talkingfaceUrl
        : isCaptions || isTimeline
        ? selectedBroll.renderedUrl
        : selectedBroll.url;

      const refFrom = isTalkingFace
        ? ReferenceType.TALKING_FACE
        : isCaptions || isTimeline
        ? ReferenceType.CAPTIONS
        : tab === tabs.AIGen
        ? ReferenceType.AI_MEDIA
        : ReferenceType.UPLOADS;

      let imageDimensions, videoDimensions, uploadId, uploadedData, type;
      uploadId = isCustomBroll
        ? selectedBroll.uploadId
        : isTalkingFace
        ? extractObjectId(selectedBroll?.processedVideo)
        : isCaptions || isTimeline
        ? selectedBroll.uploadId
        : null;
      type = isCustomBroll
        ? selectedBroll.uploads.type
        : isTalkingFace
        ? 'Video'
        : isCaptions || isTimeline
        ? 'Video'
        : null;

      if (uploadId) {
        try {
          uploadedData = await getUploadedData(uploadId);
        } catch (error) {
          console.log('Error getting metadata: ', error);
        }
      }

      try {
        if ((!uploadedData || !uploadedData?.metadata) && uploadId) {
          if (type === 'Video' || isTalkingFace || isCaptions || isTimeline) {
            videoDimensions = await extractVideoMetadataFromUrl(url);

            await updateMetadata({
              uploadId,
              metadata: videoDimensions,
            });
          } else {
            imageDimensions = await extractImageMetadataFromUrl(url);

            await updateMetadata({
              uploadId,
              metadata: imageDimensions,
            });
          }
        }
      } catch (error) {
        console.log('Unable to update metadata: ', error);
      }

      try {
        if (isUpscalePage) {
          await handleVideoSelect(url, refFrom, selectedBroll.id);
        } else {
          await handleImageSelect(url);
        }
      } catch (err) {
        setIsApplying(false);
        handleCloseDialog();
        return;
      }
      setIsApplying(false);
      handleCloseDialog();
      return;
    }

    broll.brollUrl = mediaUrl
      ? mediaUrl
      : isCustomBroll
      ? selectedBroll.uploads.url
      : selectedBroll.url;
    broll.thumbnailUrl = thumbnailUrl
      ? thumbnailUrl
      : isCustomBroll
      ? selectedBroll.thumbnail
      : selectedBroll.thumbnailUrl;
    broll.brollType = isImage ? BrollType.Image : BrollType.Video;
    broll.brollTransition = 'none';
    broll.imageEffect = 'none';
    broll.transitionEffect = 'none';
    broll.isValid = true;

    updateSelectedBroll(broll);
    handleCloseDialog();
    // }
  };

  const [tab, setTab] = useState(tabs.MyBrolls);
  const [searchText, setSearchText] = useState<string>('');

  // const {
  //   data: aiMediaData,
  //   isLoading: isLoadingAiMediaData,
  //   refetch: refetchAiMediaData,
  // } = useGetAllAiMediaData({
  //   type: isImage ? UploadTypes.Image : UploadTypes.Video,
  //   shouldFetchData: tab === tabs.AIGen,
  //   name: searchText?.trim(),
  //   limit: 1000,
  //   page: 2,
  // });

  const {
    aiMediaData,
    isLoadingAiMediaData,
    isFetchingAiMediaData,
    refetchAiMediaData,
    fetchNextPageAiMediaData,
    hasMoreAiMediaData,
    setSearchQuery,
  } = usePaginatedAiMediaData({
    type: isImage ? UploadTypes.Image : UploadTypes.Video,
    shouldFetchData: tab === tabs.AIGen,
  });

  const schema = Yup.object().shape({
    // cover: Yup.mixed().required('Cover is required'), // Validation schema
  });

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(schema),
    defaultValues: {
      cover: null,
    },
  });

  const handleLoadMore = () => {
    if (tab !== tabs.MyBrolls) handleSubmitAPI(pageSize + 10);

    setPageSize(pageSize + 10);
  };

  const handleTabClick = (tab: string) => {
    setTab(tab);
    setSearchText('');
    setStockData(null);
    setSelectedIndex(null);
    setPageSize(10);
    setSearchQuery('');
  };

  const handleSearchClick = () => {
    handleSubmitAPI(10);
    setPageSize(10);
  };

  const handleCustomBrollSearch = async () => {
    setLoading(true);
    if (!searchText.trim()) {
      setLoading(false);
      return;
    }
    const payload: SearchCustomBrollsPaylod = {
      name: searchText.trim(),
      type: isImage ? 'Image' : 'Video',
    };
    try {
      const response = await searchCustomBrolls(payload);
      setCustomBrollData(response[0]?.brollMedia);
      setCustomBrollCount(response[0]?.count);

      if (searchText && !response[0]?.brollMedia?.length) {
        enqueueSnackbar('There are no videos matching your search', { variant: 'error' });
      }
    } catch (error) {
      console.log('Error fetching custom brolls:', error);
      enqueueSnackbar(`Failed to fetch custom brolls. Please try again after some time.`, {
        variant: 'error',
      });
    }
    setLoading(false);
  };

  const {
    data: videoData,
    isLoading: videoLoading,
    refetch: refetchData,
    fetchNextPage,
    hasNextPage,
  } = useGetAllAvatarCreatedVideos(params, debouncedSearchQuery);

  const {
    brollCaptions,
    isBrollCaptionsLoading,
    refetchDataBrollCaptions,
    fetchNextPage: fetchNextPageRemotion,
    hasNextPage: hasNextPageRemotion,
  } = useBrollCaption(params, BrollCaptionsAndRenderType.REMOTION, true);

  const {
    brollCaptions: clipdeck,
    isBrollCaptionsLoading: isClipdeckDataLoading,
    refetchDataBrollCaptions: refetchDataClipdeckData,
    fetchNextPage: fetchNextPageClipdeck,
    hasNextPage: hasNextPageClipdeck,
  } = useBrollCaption(params, BrollCaptionsAndRenderType.CLIPDECK, true);

  useEffect(() => {
    if ('TalkingFace' in tabs && tab === tabs.TalkingFace) {
      const handler = setTimeout(() => {
        setDebouncedSearchQuery(searchText.trim());
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [searchText]);

  return (
    <>
      <CustomDialogContainer
        open={shouldOpenDialog}
        sx={{ ...styles.modal, display: showAiGenModel ? 'none' : 'block' }}
        maxWidth="md"
      >
        <Box sx={styles.title}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={styles.subTitle}>Search {isImage ? 'Images' : 'Clips'} </Typography>
            <IconButton aria-label="close" onClick={handleCloseDialog} sx={styles.closeButton}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ ...styles.typeTab, flexDirection: isExtraSmall ? 'column' : 'row' }}>
            {Object.values(tabs).map((res, index) => (
              <Typography
                key={index}
                onClick={() => handleTabClick(res)}
                sx={{
                  ...styles.tab,
                  borderRightWidth: index !== Object.keys(tabs).length - 1 ? '1px' : '',
                  color: tab === res ? (theme) => theme.palette.text.primary : '',
                  backgroundColor:
                    tab === res
                      ? (theme) => theme.palette.background.activeCard
                      : (theme) => theme.palette.background.primary,
                }}
              >
                {res}
              </Typography>
            ))}
          </Box>
        </Box>
        <Divider />
        <DialogContent dividers sx={styles.dialogContent}>
          <Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Search"
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setSearchQuery(e.target.value);
                }}
                value={searchText}
                sx={styles.input}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchText)
                    tab === tabs.MyBrolls ? handleCustomBrollSearch() : handleSearchClick();
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NextImage
                        src={'/assets/icons/SearchIcon.svg'}
                        alt=""
                        height={32}
                        width={32}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {tab !== tabs.AIGen && (
                <Button
                  disabled={!searchText || loading}
                  sx={styles.saveBtn}
                  variant="contained"
                  onClick={() =>
                    tab === tabs.MyBrolls ? handleCustomBrollSearch() : handleSearchClick()
                  }
                >
                  Search
                </Button>
              )}
            </Box>

            {tab === tabs.Pexels && (
              <Box sx={styles.thirdPartyCredit}>
                {isImage ? 'Images' : 'Videos'} provided by
                <Link href={PEXELS_URL} target="_blank">
                  <NextImage
                    src={'/assets/PexelsWhiteLogo.png'}
                    alt="Pexels"
                    width={60}
                    height={40}
                  />
                </Link>
              </Box>
            )}

            {tab === tabs.Pixabay && (
              <Box sx={styles.thirdPartyCredit}>
                {isImage ? 'Images' : 'Videos'} provided by
                <Link href={PIXABAY_URL} target="_blank">
                  <NextImage
                    src={'/assets/PixabayIconLogo.svg'}
                    alt="Pexels"
                    width={25}
                    height={25}
                  />
                </Link>
              </Box>
            )}

            {tab === tabs.MyBrolls ? (
              <Box
                sx={{
                  height: 300,
                  overflow: 'auto',
                }}
              >
                <CustomBRolls
                  isImage={isImage}
                  customBrollData={customBrollData}
                  setCustomBrollData={setCustomBrollData}
                  handleItemClick={handleItemClick}
                  selectedIndex={selectedIndex}
                  pageSize={pageSize}
                  setCustomBrollCount={setCustomBrollCount}
                  searchText={searchText}
                />
                <Box sx={styles.showMore} mt={2}>
                  {customBrollData &&
                    customBrollData?.length >= 10 &&
                    customBrollData?.length < customBrollCount && (
                      <LoadingButton
                        variant="text"
                        onClick={handleLoadMore}
                        disabled={loading}
                        loading={loading}
                      >
                        Show More...
                      </LoadingButton>
                    )}
                </Box>
              </Box>
            ) : tab === tabs.AIGen ? (
              <AiGeneratedMediaTab
                isImage={isImage}
                selectedIndex={selectedIndex}
                aiMediaData={aiMediaData}
                handleOnSelectMedia={handleItemClick}
                isLoadingAiMediaData={isLoadingAiMediaData}
                refetchAiMediaData={refetchAiMediaData}
                fetchNextPageAiMediaData={fetchNextPageAiMediaData}
                hasMoreAiMediaData={hasMoreAiMediaData}
              />
            ) : 'TalkingFace' in tabs && tab === tabs.TalkingFace ? (
              <Box
                sx={{
                  height: 300,
                  overflow: 'auto',
                }}
                id="scrollableDiv"
              >
                <TalkingFaceTab
                  allData={videoData}
                  isLoading={videoLoading}
                  refetch={refetchData}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  setSelectedIndex={setSelectedIndex}
                  selectedIndex={selectedIndex}
                />
              </Box>
            ) : 'Captions' in tabs && tab === tabs.Captions ? (
              <Box
                sx={{
                  height: 300,
                  overflow: 'auto',
                }}
                id="scrollableDiv"
              >
                <CaptionsTab
                  allData={brollCaptions}
                  isLoading={isBrollCaptionsLoading}
                  refetch={refetchDataBrollCaptions}
                  fetchNextPage={fetchNextPageRemotion}
                  hasNextPage={hasNextPageRemotion}
                  setSelectedIndex={setSelectedIndex}
                  selectedIndex={selectedIndex}
                />
              </Box>
            ) : 'TimelineEditor' in tabs && tab === tabs.TimelineEditor ? (
              <Box
                sx={{
                  height: 300,
                  overflow: 'auto',
                }}
                id="scrollableDiv"
              >
                <CaptionsTab
                  allData={clipdeck}
                  isLoading={isClipdeckDataLoading}
                  refetch={refetchDataClipdeckData}
                  fetchNextPage={fetchNextPageClipdeck}
                  hasNextPage={hasNextPageClipdeck}
                  setSelectedIndex={setSelectedIndex}
                  selectedIndex={selectedIndex}
                />
              </Box>
            ) : (
              <Box sx={styles.clipsAndShowMore}>
                {isImage ? (
                  <Box sx={styles.clipsContainer}>
                    {stockData?.items?.map((res, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: '210px',
                          cursor: 'pointer',
                          border:
                            selectedIndex === index
                              ? (theme) => `2px solid ${theme.palette.background.activeBorder}`
                              : '2px solid transparent', // Conditionally apply border
                          borderRadius: '4px',
                          padding: '4px',
                        }}
                        onClick={() => handleItemClick(index)} // Set selected index on click
                      >
                        <img src={res.url} alt={`item-${index}`} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={styles.clipsContainer}>
                    {stockData?.items?.map((res, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: '210px',
                          cursor: 'pointer',
                          border:
                            selectedIndex === index
                              ? (theme) => `2px solid ${theme.palette.background.activeBorder}`
                              : '2px solid transparent', // Apply border conditionally
                          borderRadius: '4px',
                          padding: '4px',
                        }}
                        onClick={() => handleItemClick(index)} // Set selected index on click
                      >
                        <VideoPlayerForStockVideos height="142px" url={res.url} />
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={styles.showMore}>
                  {stockData?.items && !!stockData?.items?.length && (
                    <LoadingButton
                      variant="text"
                      onClick={handleLoadMore}
                      disabled={loading}
                      loading={loading}
                    >
                      Show More...
                    </LoadingButton>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* DONT REMOVE IT */}
          {/* {tab === tabs.MyBrolls && (
          <CustomBRolls
            isImage={isImage}
            customBrollData={customBrollData}
            setCustomBrollData={setCustomBrollData}
          />
        )} */}
          <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} mt={2}>
            {/* {!isBrollPage && isImage ? (
              <Button variant="contained" sx={styles.genAiBtn} onClick={handleOpenAiGenModal}>
                <NextImage
                  src={'/assets/icons/AiStars.svg'}
                  alt="Logo"
                  width={24}
                  height={24}
                  sx={{ paddingRight: 1 }}
                />
                Generate AI {isImage ? 'Image' : 'Video'}
              </Button>) : <Box />
              } */}
            <Box />

            <Box sx={styles.btns}>
              <Button sx={styles.cancelBtn} variant="outlined" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <LoadingButton
                disabled={selectedIndex === null}
                loading={isApplying}
                sx={styles.saveBtn}
                variant="contained"
                onClick={() => handleApplyBroll()}
              >
                Apply
              </LoadingButton>
            </Box>
          </Stack>
        </DialogContent>
      </CustomDialogContainer>
      <AiGeneratedMediaDialog
        shouldOpenDialog={showAiGenModel}
        handleOnCloseDialog={handleCloseAiGenModal}
        isImage={isImage}
        handleApplyBroll={handleApplyBroll}
        fetchAiMediaData={refetchAiMediaData}
      />
    </>
  );
};

const styles: Styles = {
  modal: { maxWidth: '800px', margin: 'auto', height: '700px' },
  title: {
    padding: '24px',
    paddingTop: '10px',
    background: (theme) => theme.palette.background.secondary,
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    background: (theme) => theme.palette.background.secondary,
    flexDirection: 'column',
    // gap: 2,
  },
  theme: {
    width: '128px',
    paddingY: 2,
    backgroundColor: (theme) => theme.palette.background.border,
    color: (theme) => theme.palette.text.primary,
    textAlign: 'center',
  },
  clipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
  },
  saveBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  cancelBtn: {
    color: (theme) => theme.palette.text.primary,
    borderColor: (theme) => theme.palette.background.border,
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  btns: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1,
  },
  input: {
    marginBottom: 3,
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.border,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  closeButton: {
    color: (theme) => theme.palette.text.primary,
    mr: -1,
  },
  typeTab: {
    display: 'flex',
    borderRadius: '6px',
    borderColor: (theme) => theme.palette.background.border,
    justifyContent: 'space-between',
    borderWidth: '1px',
  },
  tab: {
    flex: 1,
    textAlign: 'center',
    paddingY: 1,
    borderColor: (theme) => theme.palette.background.border,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clipsAndShowMore: {
    overflowY: 'auto',
    maxHeight: '350px',
    minHeight: '265px',
  },
  showMore: { width: '100%', textAlign: 'center' },
  genAiBtn: {
    color: (theme) => theme.palette.text.primary,
    fontWeight: 400,
    borderRadius: 50,
    boxShadow: 'none',
  },
  logoTab: {
    p: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    flexDirection: 'column',
    cursor: 'pointer',
  },
  thirdPartyCredit: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    mb: 1,
    fontSize: '12px',
  },
};
